/**
 * SecondaryActionButton - Medium-emphasis action buttons
 * Used for: Preview Canvas Size, Draw/Erase/Clear modes, etc.
 *
 * Uses CSS class .btn-tool-action for all styling.
 *
 * @param {string} children - Button label/content
 * @param {function} onClick - Click handler
 * @param {boolean} disabled - Whether button is disabled
 * @param {boolean} active - Whether button is in active/selected state
 * @param {boolean} flash - Trigger a brief visual flash (for Clear button)
 * @param {ReactNode} icon - Optional icon element
 * @param {string} size - "default" | "compact" | "small" | "compactInput"
 * @param {string} variant - "default" | "accent" | "silent" — silent rests transparent and fills on hover
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
import { forwardRef } from 'react';
import BaseButton from './base/BaseButton';

const SecondaryActionButton = forwardRef(function SecondaryActionButton({
  children,
  onClick,
  disabled = false,
  active = false,
  flash = false,
  icon = null,
  size = 'default',
  variant = 'default',
  className = '',
  style = {},
  type = 'button',
  ...buttonProps
}, ref) {
  const isIconOnly = icon && !children;

  const classes = [
    'btn-tool-action',
    isIconOnly && 'is-icon-only',
    active && 'is-active',
    flash && 'is-flash',
    size === 'compact' && 'btn-compact',
    size === 'small' && 'btn-small',
    size === 'compactInput' && 'btn-compact-input',
    variant === 'accent' && 'btn-accent',
    variant === 'silent' && 'btn-silent',
    className
  ].filter(Boolean).join(' ');

  return (
    <BaseButton
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classes}
      style={style}
      {...buttonProps}
    >
      {icon}
      {children}
    </BaseButton>
  );
});

export default SecondaryActionButton;
